// src/services/quoteService.js
import { 
    collection, query, orderBy, getDocs, getDoc, doc, setDoc, 
    updateDoc, deleteDoc, runTransaction, serverTimestamp 
} from 'firebase/firestore';
import { 
    getStorage, ref, uploadBytes, getDownloadURL, deleteObject 
} from 'firebase/storage';
import { db } from '../config/firebase';

const QUOTES_COL = 'quotes';
const ACCOUNTING_COL = 'accounting';
const CLIENTS_COL = 'clients';
const PARAMS_COL = 'parameters';
const COUNTER_COL = 'quoteCounter';

export const quoteService = {
    /**
     * Obtiene todas las cotizaciones ordenadas por fecha.
     */
    getAll: async () => {
        try {
            const q = query(collection(db, QUOTES_COL), orderBy('createdAt', 'desc'));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("Error obteniendo cotizaciones:", error);
            throw error;
        }
    },

    /**
     * Obtiene una cotización por ID.
     */
    getById: async (id) => {
        try {
            const docSnap = await getDoc(doc(db, QUOTES_COL, id));
            return docSnap.exists() ? docSnap.data() : null;
        } catch (error) {
            console.error("Error obteniendo cotización:", error);
            throw error;
        }
    },

    /**
     * Obtiene los parámetros del sistema (para IVA, cuentas bancarias, etc).
     */
    getSystemParams: async () => {
        try {
            const docSnap = await getDoc(doc(db, PARAMS_COL, 'system-settings'));
            return docSnap.exists() ? docSnap.data() : {};
        } catch (error) {
            console.error("Error obteniendo parámetros:", error);
            return {};
        }
    },

    /**
     * Sube fotos a Storage.
     */
    uploadPhotos: async (folio, files) => {
        if (!files || files.length === 0) return [];
        const storage = getStorage();
        
        const uploadPromises = files.map(async (previewObj, index) => {
            const file = previewObj.file;
            if (!(file instanceof File) && !(file instanceof Blob)) return null;

            const fileName = `${folio}-${Date.now()}-${index}-${file.name || 'img.jpg'}`;
            const storageRef = ref(storage, `quote-photos/${fileName}`);
            
            await uploadBytes(storageRef, file);
            const url = await getDownloadURL(storageRef);
            return { url, name: fileName }; // Guardamos name para poder borrar después
        });

        const results = await Promise.all(uploadPromises);
        return results.filter(r => r !== null);
    },

    /**
     * GUARDA LA COTIZACIÓN (Crear o Editar).
     * Genera folio automático si es nueva.
     * Actualiza datos de contacto del cliente.
     */
    saveQuote: async ({ quoteId, quoteData, photos, isEditMode }) => {
        try {
            let finalFolio = quoteId;

            // 1. Generar Folio si es nueva
            if (!isEditMode) {
                const counterRef = doc(db, COUNTER_COL, 'counter');
                const newId = await runTransaction(db, async (transaction) => {
                    const docSnap = await transaction.get(counterRef);
                    let newCount = 1;
                    if (docSnap.exists()) {
                        newCount = docSnap.data().current + 1;
                        transaction.update(counterRef, { current: newCount });
                    } else {
                        transaction.set(counterRef, { current: 1 });
                    }
                    return newCount;
                });
                finalFolio = String(newId).padStart(7, '0');
            }

            // 2. Subir Fotos Nuevas (si las hay)
            const newFiles = photos.filter(p => p.file);
            const uploadedPhotos = await quoteService.uploadPhotos(finalFolio, newFiles);
            
            const existingPhotos = photos.filter(p => !p.file).map(p => ({ url: p.src, name: p.name })); // Mantener estructura
            const finalPhotos = [...existingPhotos, ...uploadedPhotos];

            // 3. Actualizar Cliente (Datos de contacto frescos)
            // OJO: quoteData.clientId debe ser el RUT limpio
            if (quoteData.clientId) {
                await setDoc(doc(db, CLIENTS_COL, quoteData.clientId), {
                    nombre: quoteData.clientName,
                    rut: quoteData.clientId,
                    direccion: quoteData.address,
                    telefono: quoteData.phone,
                    email: quoteData.email,
                    encargado: quoteData.encargado,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            }

            // 4. Guardar Cotización
            const dataToSave = {
                ...quoteData,
                fotos: finalPhotos,
                updatedAt: serverTimestamp()
            };

            const docRef = doc(db, QUOTES_COL, isEditMode ? quoteId : finalFolio);
            
            if (isEditMode) {
                await updateDoc(docRef, dataToSave);
            } else {
                dataToSave.createdAt = serverTimestamp();
                dataToSave.status = 'Pendiente'; // Estado inicial
                await setDoc(docRef, dataToSave);
            }

            return finalFolio;

        } catch (error) {
            console.error("Error guardando cotización:", error);
            throw error;
        }
    },

    /**
     * Elimina una cotización, sus fotos y su registro contable si existe.
     */
    deleteQuote: async (quoteId, photos = []) => {
        try {
            const storage = getStorage();

            // 1. Borrar fotos
            if (photos && photos.length > 0) {
                const deletePromises = photos.map(p => {
                    if (p.name) {
                        const fileRef = ref(storage, `quote-photos/${p.name}`);
                        // O si guardaste la ruta completa, úsala. 
                        // Aquí intentamos borrar por nombre si lo tenemos, o parseando la URL si es necesario.
                        // Para simplificar, si tienes el 'name' (referencia) úsalo.
                        return deleteObject(fileRef).catch(e => console.warn("Foto no encontrada", e));
                    } else {
                        // Fallback: intentar extraer de la URL (menos seguro)
                        try {
                            const url = new URL(p.url);
                            const path = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
                            const fileRef = ref(storage, path);
                            return deleteObject(fileRef).catch(() => {});
                        } catch (e) { return Promise.resolve(); }
                    }
                });
                await Promise.all(deletePromises);
            }

            // 2. Borrar Cotización
            await deleteDoc(doc(db, QUOTES_COL, quoteId));

            // 3. Borrar registro contable asociado (si no fue pagado)
            await deleteDoc(doc(db, ACCOUNTING_COL, `COT-${quoteId}`)).catch(() => {});

        } catch (error) {
            console.error("Error eliminando cotización:", error);
            throw error;
        }
    },

    /**
     * Aprueba una cotización y genera el registro de cobro en Contabilidad.
     */
    approveQuote: async (quoteId, quoteData) => {
        try {
            // 1. Actualizar estado a Aprobada
            await updateDoc(doc(db, QUOTES_COL, quoteId), { 
                status: 'Aprobada', 
                approvedAt: serverTimestamp() 
            });

            // 2. Crear registro en Contabilidad
            const accountingId = `COT-${quoteId}`;
            const accountingData = {
                id: accountingId,
                clientName: quoteData.clientName,
                clientId: quoteData.clientId, // RUT
                visitDate: quoteData.createdAt, // Usamos fecha creación como referencia
                totalAmount: quoteData.total,
                paidAmount: 0,
                balance: quoteData.total,
                status: 'Pendiente por Facturar',
                origin: 'Cotización',
                originQuoteId: quoteId,
                invoices: [],
                payments: [],
                createdAt: serverTimestamp()
            };

            await setDoc(doc(db, ACCOUNTING_COL, accountingId), accountingData, { merge: true });

        } catch (error) {
            console.error("Error aprobando cotización:", error);
            throw error;
        }
    }
};